import React, { useState, useEffect } from "react";
import NotificationBar from "../components/home/NotifictionBar";
import { motion } from "framer-motion";
import countries from "../json/CountryFlagCode.json";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import api from "../lib/api.ts";
import usePageMeta from "../hooks/usePageMeta.js";
const ContactPage = () => {
  usePageMeta("contact_us");

  const { user } = useAuth();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  const [selectedCountry, setSelectedCountry] = useState("AE");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [dynamicContent, setDynamicContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch dynamic content for contact page
  const fetchDynamicContent = async () => {
    try {
      setLoading(true);
      const lang = language === "ar" ? "ar" : "en";
      const response = await api.get(
        `/pagesettings/contactus?lang=${lang}&withLang=true`
      );
      console.log(response.data);
      setDynamicContent(response.data);
    } catch (error) {
      console.error("Error fetching contact page content:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dynamic content on component mount and when language changes
  useEffect(() => {
    fetchDynamicContent();
  }, [language]);

  // Auto-fill form data when user is logged in
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
        phone: user.mobileNo || "",
      }));

      // Set country code if user has one
      if (user.countryCode) {
        setSelectedCountry(user.countryCode);
      }
    }
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage("");

    try {
      const selectedCountryData = countries.find(
        (country) => country.code === selectedCountry
      );
      const dialCode = selectedCountryData ? selectedCountryData.dial_code : "";

      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        countryCode: dialCode,
        subject: formData.subject,
        message: formData.message,
      };

      const response = await api.post("/email/contact-us", payload);

      if (response.status === 200 || response.status === 201) {
        setSubmitMessage(translate("contactPage.messages.success"));
        // Reset form after successful submission
        setFormData({
          name: user?.name || "",
          email: user?.email || "",
          phone: user?.mobileNo || "",
          subject: "",
          message: "",
        });
        setSelectedCountry(user?.countryCode || "AE");
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      setSubmitMessage(translate("contactPage.messages.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate contact cards from dynamic content
  const getContactCards = () => {
    if (!dynamicContent?.after_contact_form_section_elements) {
      return [];
    }

    const lang = language === "ar" ? "ar" : "en";
    const elements =
      dynamicContent.after_contact_form_section_elements[lang] || [];

    return elements.map((element, index) => {
      // Define icons for different contact types
      const getIcon = (title) => {
        const titleLower = title?.toLowerCase() || "";
        if (titleLower.includes("email")) {
          return (
            <svg
              width="28"
              height="22"
              viewBox="0 0 28 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.22878 1.89535C0.666687 3.45745 0.666687 5.9716 0.666687 10.9999C0.666687 16.0282 0.666687 18.5424 2.22878 20.1045C3.79088 21.6666 6.30504 21.6666 11.3334 21.6666H16.6667C21.695 21.6666 24.2092 21.6666 25.7713 20.1045C27.3334 18.5424 27.3334 16.0282 27.3334 10.9999C27.3334 5.9716 27.3334 3.45745 25.7713 1.89535C24.2092 0.333252 21.695 0.333252 16.6667 0.333252H11.3334C6.30504 0.333252 3.79088 0.333252 2.22878 1.89535ZM22.7682 5.0264C23.1218 5.45068 23.0645 6.08124 22.6402 6.43481L19.7116 8.87534C18.5297 9.86022 17.5719 10.6585 16.7264 11.2022C15.8458 11.7686 14.9881 12.1264 14 12.1264C13.0119 12.1264 12.1543 11.7686 11.2736 11.2022C10.4282 10.6585 9.4703 9.86023 8.28849 8.87536L5.35984 6.43481C4.93556 6.08124 4.87823 5.45068 5.2318 5.0264C5.58536 4.60212 6.21593 4.5448 6.6402 4.89836L9.51873 7.29714C10.7627 8.33376 11.6263 9.05114 12.3555 9.52009C13.0613 9.97404 13.5399 10.1264 14 10.1264C14.4601 10.1264 14.9388 9.97404 15.6446 9.52009C16.3737 9.05114 17.2374 8.33376 18.4813 7.29714L21.3598 4.89836C21.7841 4.5448 22.4147 4.60212 22.7682 5.0264Z"
                fill="#151529"
              />
            </svg>
          );
        } else if (titleLower.includes("call")) {
          return (
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_2509_2293)">
                <path
                  d="M18.0338 5.741C18.0677 5.61409 18.1263 5.49511 18.2063 5.39086C18.2862 5.28661 18.3859 5.19914 18.4997 5.13344C18.6134 5.06773 18.739 5.02509 18.8693 5.00794C18.9995 4.99079 19.1319 4.99948 19.2588 5.0335C21.1126 5.51718 22.804 6.4863 24.1587 7.84104C25.5135 9.19578 26.4826 10.8872 26.9663 12.741C27.0003 12.8679 27.009 13.0002 26.9918 13.1305C26.9747 13.2607 26.932 13.3863 26.8663 13.5001C26.8006 13.6138 26.7131 13.7135 26.6089 13.7935C26.5047 13.8734 26.3857 13.9321 26.2588 13.966C26.1743 13.9882 26.0873 13.9995 26 13.9997C25.7798 13.9998 25.5657 13.9272 25.391 13.7932C25.2163 13.6591 25.0907 13.4712 25.0338 13.2585C24.6395 11.7449 23.8486 10.3639 22.7428 9.2578C21.6369 8.15171 20.256 7.36059 18.7425 6.966C18.6155 6.93219 18.4964 6.87366 18.392 6.79378C18.2876 6.71389 18.2 6.61421 18.1342 6.50044C18.0683 6.38666 18.0256 6.26103 18.0084 6.13071C17.9911 6.0004 17.9998 5.86797 18.0338 5.741ZM17.7425 10.966C19.4663 11.426 20.5738 12.5347 21.0338 14.2585C21.0907 14.4712 21.2163 14.6591 21.391 14.7932C21.5657 14.9272 21.7798 14.9998 22 14.9997C22.0873 14.9995 22.1743 14.9882 22.2588 14.966C22.3857 14.9321 22.5047 14.8734 22.6089 14.7935C22.7131 14.7135 22.8006 14.6138 22.8663 14.5001C22.932 14.3863 22.9747 14.2607 22.9918 14.1305C23.009 14.0002 23.0003 13.8679 22.9663 13.741C22.3263 11.346 20.6538 9.6735 18.2588 9.0335C18.0025 8.96504 17.7295 9.00118 17.4999 9.13398C17.2703 9.26678 17.1028 9.48536 17.0344 9.74162C16.9659 9.99789 17.0021 10.2709 17.1349 10.5005C17.2677 10.7301 17.4862 10.8975 17.7425 10.966ZM26.7963 20.8072L20.9075 18.1685L20.8913 18.161C20.5856 18.0302 20.2521 17.9778 19.921 18.0083C19.5899 18.0389 19.2716 18.1515 18.995 18.336C18.9624 18.3575 18.9311 18.3809 18.9013 18.406L15.8588 20.9997C13.9313 20.0635 11.9413 18.0885 11.005 16.186L13.6025 13.0972C13.6275 13.066 13.6513 13.0347 13.6738 13.001C13.8543 12.7251 13.9638 12.4089 13.9926 12.0805C14.0214 11.752 13.9686 11.4216 13.8388 11.1185V11.1035L11.1925 5.20475C11.0209 4.80883 10.7259 4.47902 10.3515 4.26455C9.97707 4.05007 9.54333 3.96245 9.11501 4.01475C7.42122 4.23763 5.86649 5.06946 4.74118 6.35487C3.61587 7.64028 2.99695 9.29136 3.00001 10.9997C3.00001 20.9247 11.075 28.9997 21 28.9997C22.7084 29.0028 24.3595 28.3839 25.6449 27.2586C26.9303 26.1333 27.7621 24.5785 27.985 22.8847C28.0374 22.4566 27.95 22.0229 27.7357 21.6485C27.5215 21.2741 27.1919 20.979 26.7963 20.8072Z"
                  fill="#151529"
                />
              </g>
              <defs>
                <clipPath id="clip0_2509_2293">
                  <rect width="32" height="32" fill="white" />
                </clipPath>
              </defs>
            </svg>
          );
        } else {
          return (
            <svg
              width="26"
              height="26"
              viewBox="0 0 26 26"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 0C10.4288 0 7.91543 0.762437 5.77759 2.1909C3.63975 3.61935 1.97351 5.64968 0.989572 8.02512C0.0056327 10.4006 -0.251811 13.0144 0.249797 15.5362C0.751405 18.0579 1.98953 20.3743 3.80762 22.1924C5.6257 24.0105 7.94208 25.2486 10.4638 25.7502C12.9856 26.2518 15.5995 25.9944 17.9749 25.0104C20.3503 24.0265 22.3807 22.3603 23.8091 20.2224C25.2376 18.0846 26 15.5712 26 13C25.9964 9.5533 24.6256 6.24882 22.1884 3.81163C19.7512 1.37445 16.4467 0.00363977 13 0ZM20 14H15.4138L18.7075 17.2925C18.8004 17.3854 18.8741 17.4957 18.9244 17.6171C18.9747 17.7385 19.0006 17.8686 19.0006 18C19.0006 18.1314 18.9747 18.2615 18.9244 18.3829C18.8741 18.5043 18.8004 18.6146 18.7075 18.7075C18.6146 18.8004 18.5043 18.8741 18.3829 18.9244C18.2615 18.9747 18.1314 19.0006 18 19.0006C17.8686 19.0006 17.7385 18.9747 17.6171 18.9244C17.4957 18.8741 17.3854 18.8004 17.2925 18.7075L12.2925 13.7075C12.1525 13.5676 12.0571 13.3894 12.0185 13.1953C11.9798 13.0012 11.9996 12.8 12.0754 12.6172C12.1511 12.4344 12.2794 12.2782 12.444 12.1683C12.6086 12.0584 12.8021 11.9998 13 12H20C20.2652 12 20.5196 12.1054 20.7071 12.2929C20.8946 12.4804 21 12.7348 21 13C21 13.2652 20.8946 13.5196 20.7071 13.7071C20.5196 13.8946 20.2652 14 20 14Z"
                fill="#151529"
              />
            </svg>
          );
        }
      };

      return {
        title: element.title?.value || "",
        text: element.subtitle?.value || "",
        desc: element.description?.value || "",
        icon: getIcon(element.title?.value),
      };
    });
  };

  return (
    <>
      <NotificationBar />
      <style>{`
        .blog-content .ql-ui { display: none; }
        .blog-content ol { list-style: decimal; margin-left: 1.25rem; }
        .blog-content ul { list-style: disc; margin-left: 1.25rem; }
        .blog-content li { margin: 0.375rem 0; }
        .blog-content li[data-list="bullet"] { list-style-type: disc; }
        .blog-content li[data-list="ordered"] { list-style-type: decimal; }
        .blog-content h1, .blog-content h2, .blog-content h3 { color: #111827; }
        .blog-content p { color: #374151; white-space: pre-wrap; }
        .blog-content a { color: #2563eb; text-decoration: underline; }
      `}</style>

      <div
        className="w-full min-h-screen bg-gray-50 flex flex-col items-center"
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        <section className="w-full max-w-7xl relative shadow-lg overflow-hidden flex flex-col md:flex-row justify-center mx-auto">
          {/* Background Image with White Overlay */}
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8ZHViYWl8ZW58MHx8MHx8fDA%3D"
              alt="Background"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-white bg-opacity-90"></div>
          </div>

          {/* Form Content */}
          <motion.div
            className="relative w-full md:w-2/3 p-8 z-10 flex flex-col"
            initial={{ opacity: 0, x: -100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, type: "spring" }}
          >
            <motion.h2
              className="text-3xl font-bold text-[#F15A29] mb-2"
              initial={{ y: -20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {translate("contactPage.heading")}
            </motion.h2>
            <motion.p
              className="text-gray-600 mb-6"
              initial={{ y: -20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {translate("contactPage.subtitle")}
            </motion.p>

            {/* Animate form fields one by one */}
            <motion.form
              className="space-y-4 flex flex-col flex-1"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={{
                hidden: {},
                show: {
                  transition: { staggerChildren: 0.2 },
                },
              }}
              onSubmit={handleSubmit}
            >
              {/* Submit Message */}
              {submitMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-3 rounded-lg text-center font-medium ${
                    submitMessage === translate("contactPage.messages.success")
                      ? "bg-green-100 text-green-800 border border-green-200"
                      : "bg-red-100 text-red-800 border border-red-200"
                  }`}
                >
                  {submitMessage}
                </motion.div>
              )}

              {[
                <div
                  className={`flex flex-col md:flex-row gap-4 ${
                    isRTL ? "md:flex-row-reverse" : ""
                  }`}
                >
                  <div className="flex-1 flex flex-col">
                    <label
                      className={`mb-1 font-semibold text-gray-700 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      {translate("contactPage.labels.fullName")}
                    </label>
                    <input
                      type="text"
                      placeholder={translate(
                        "contactPage.placeholders.enterName"
                      )}
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      className={`w-full h-12 border border-[#13131399] rounded-lg bg-white px-4 focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <label
                      className={`mb-1 font-semibold text-gray-700 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      {translate("contactPage.labels.email")}
                    </label>
                    <input
                      type="email"
                      placeholder={translate(
                        "contactPage.placeholders.emailExample"
                      )}
                      value={formData.email}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      className={`w-full h-12 border border-[#13131399] rounded-lg bg-white px-4 focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    />
                  </div>
                </div>,
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 flex flex-col">
                    <label
                      className={`mb-1 font-semibold text-gray-700 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      {translate("contactPage.labels.phoneNumber")}
                    </label>
                    <div
                      className={`flex w-full h-12 border border-[#13131399] rounded-lg bg-white overflow-hidden focus-within:ring-orange-500 ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      {/* Country Code Select with Flag */}
                      <div className="relative">
                        <div
                          className={`absolute top-1/2 transform -translate-y-1/2 w-10 h-5 ${
                            isRTL ? "right-2" : "left-2"
                          }`}
                        >
                          <img
                            src={`https://flagcdn.com/w40/${selectedCountry.toLowerCase()}.png`}
                            alt="flag"
                            className="w-full h-full object-cover rounded-sm"
                          />
                        </div>
                        <select
                          value={selectedCountry}
                          onChange={(e) => setSelectedCountry(e.target.value)}
                          className={`h-full bg-white focus:outline-none appearance-none cursor-pointer ${
                            isRTL ? "pr-12 rounded-r-lg" : "pl-12 rounded-l-lg"
                          }`}
                        >
                          {countries
                            .sort((a, b) =>
                              a.dial_code.localeCompare(b.dial_code)
                            )
                            .map((country) => (
                              <option key={country.code} value={country.code}>
                                {country.dial_code}
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* Phone Number Input */}
                      <input
                        type="tel"
                        placeholder={translate(
                          "contactPage.placeholders.enterPhone"
                        )}
                        value={formData.phone}
                        onChange={(e) =>
                          handleInputChange("phone", e.target.value)
                        }
                        className={`flex-1 px-4 h-full bg-white focus:outline-none ${
                          isRTL ? "text-right" : "text-left"
                        }`}
                      />
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col">
                    <label
                      className={`mb-1 font-semibold text-gray-700 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      {translate("contactPage.labels.subject")}
                    </label>
                    <select
                      value={formData.subject}
                      onChange={(e) =>
                        handleInputChange("subject", e.target.value)
                      }
                      className={`w-full h-12 border border-[#13131399] rounded-lg bg-white px-4 focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none cursor-pointer ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      <option value="">
                        {translate("contactPage.subjectOptions.select")}
                      </option>
                      <option value="Complaint">
                        {translate("contactPage.subjectOptions.complaint")}
                      </option>
                      <option value="Suggestion">
                        {translate("contactPage.subjectOptions.suggestion")}
                      </option>
                      <option value="Feedback">
                        {translate("contactPage.subjectOptions.feedback")}
                      </option>
                      <option value="Other">
                        {translate("contactPage.subjectOptions.other")}
                      </option>
                    </select>
                  </div>
                </div>,
                <div className="flex flex-col">
                  <label
                    className={`mb-1 font-semibold text-gray-700 ${
                      isRTL ? "text-right" : "text-left"
                    }`}
                  >
                    {translate("contactPage.labels.howCanWeHelp")}
                  </label>
                  <textarea
                    placeholder={translate(
                      "contactPage.placeholders.writeMessage"
                    )}
                    value={formData.message}
                    onChange={(e) =>
                      handleInputChange("message", e.target.value)
                    }
                    className={`w-full h-24 border border-[#13131399] rounded-lg bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none ${
                      isRTL ? "text-right" : "text-left"
                    }`}
                  />
                </div>,
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full h-12 rounded-lg transition font-medium ${
                    isSubmitting
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-black text-white hover:bg-gray-800"
                  }`}
                >
                  {isSubmitting ? (
                    <div
                      className={`flex items-center justify-center ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <div
                        className={`animate-spin rounded-full h-5 w-5 border-b-2 border-white ${
                          isRTL ? "ml-2" : "mr-2"
                        }`}
                      ></div>
                      {translate("contactPage.buttons.sending")}
                    </div>
                  ) : (
                    translate("contactPage.buttons.sendMessage")
                  )}
                </button>,
              ].map((child, i) => (
                <motion.div
                  key={i}
                  variants={{
                    hidden: { opacity: 0, y: 30 },
                    show: { opacity: 1, y: 0 },
                  }}
                  transition={{ duration: 0.5 }}
                >
                  {child}
                </motion.div>
              ))}
            </motion.form>
          </motion.div>

          {/* Right-side small image */}
          <motion.div
            className="relative w-full md:w-1/3 p-8 flex items-start z-10"
            initial={{ opacity: 0, x: 100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, type: "spring" }}
          >
            <img
              src={
                dynamicContent?.contact_us_form_right_image?.[
                  language === "ar" ? "ar" : "en"
                ]?.[0]?.imageUrl?.value
              }
              alt="Contact"
              className="w-full rounded-lg shadow-lg object-cover"
              style={{ height: "100%" }}
            />
          </motion.div>
        </section>

        <br />
        <br />
        <br />

        {/* Talk to Us Section */}
        <section className="w-full max-w-7xl mx-auto text-center px-6 md:px-12">
          <h2 className="text-3xl font-bold text-[#F15A29] mb-2">
            {
              dynamicContent?.after_contact_form_section?.[
                language === "ar" ? "ar" : "en"
              ]?.[0]?.title?.value
            }
          </h2>
          <p className="text-gray-600 mb-8">
            {
              dynamicContent?.after_contact_form_section?.[
                language === "ar" ? "ar" : "en"
              ]?.[0]?.subtitle?.value
            }
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 justify-items-center">
            {/* Dynamic Contact Cards */}
            {getContactCards().map((card, i) => (
              <motion.div
                key={i}
                className="bg-white p-6 rounded-lg shadow-lg shadow-[#f3b86a27] border border-[#cacacaa6] w-full max-w-sm"
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0px 10px 25px rgba(241,90,41,0.25)",
                }}
                transition={{ duration: 0.6, delay: i * 0.2, ease: "easeOut" }}
              >
                <motion.div
                  className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full border border-[#15152933] text-3xl"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 1 }}
                >
                  {card.icon}
                </motion.div>
                <h3 className="font-semibold mb-2">{card.title}</h3>
                <div
                  className="blog-content prose prose-sm text-gray-600 mb-2"
                  dangerouslySetInnerHTML={{ __html: card.text }}
                />
                <div
                  className="blog-content prose prose-sm text-gray-500"
                  dangerouslySetInnerHTML={{ __html: card.desc }}
                />
              </motion.div>
            ))}
          </div>
        </section>
        <br />
        <br />
        <br />

        <section className="w-full max-w-7xl px-4 md:px-8 lg:px-12 mt-8">
          <div className="flex flex-col md:flex-row bg-white shadow-md border border-[#929292ad] rounded overflow-hidden w-full">
            {/* Map */}
            <motion.div
              className="w-full md:w-3/4"
              initial={{ opacity: 0, x: -100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <iframe
                src={
                  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3605.6634443680446!2d55.632497!3d25.3490722!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ef5f393aa0438c1%3A0x979387447f80d3bc!2zS2hhdCBBbCBKYXplZXJhIENhcnMgQXVjdGlvbiAtINiu2Lcg2KfZhNis2LLZitix2Kkg2YTZhdiy2KfYryDYp9mE2LPZitin2LHYp9iq!5e0!3m2!1sen!2s!4v1755084355047!5m2!1sen!2s" ||
                  dynamicContent?.google_map_link?.[
                    language === "ar" ? "ar" : "en"
                  ]?.[0]?.link?.value
                }
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </motion.div>

            {/* Info */}
            <motion.div
              className="w-full md:w-1/4 flex flex-col items-center justify-center p-6 text-center bg-white"
              initial={{ opacity: 0, x: 100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
            >
              <motion.img
                src={
                  dynamicContent?.map_right_section?.[
                    language === "ar" ? "ar" : "en"
                  ]?.[0]?.imageUrl?.value
                }
                alt={
                  dynamicContent?.map_right_section?.[
                    language === "ar" ? "ar" : "en"
                  ]?.[0]?.title?.value
                }
                className="w-40 h-auto mb-4 rounded shadow"
                whileHover={{ scale: 1.05, rotate: 2 }}
                transition={{ type: "spring", stiffness: 200 }}
              />
              <h3 className="text-lg font-semibold">
                {
                  dynamicContent?.map_right_section?.[
                    language === "ar" ? "ar" : "en"
                  ]?.[0]?.title?.value
                }
              </h3>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                {
                  dynamicContent?.map_right_section?.[
                    language === "ar" ? "ar" : "en"
                  ]?.[0]?.subtitle?.value
                }
              </p>
              <motion.a
                href={
                  dynamicContent?.map_right_section?.[
                    language === "ar" ? "ar" : "en"
                  ]?.[0]?.btnLink?.value
                }
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#F15A29] hover:bg-orange-700 text-white py-2 px-6 rounded font-medium transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {
                  dynamicContent?.map_right_section?.[
                    language === "ar" ? "ar" : "en"
                  ]?.[0]?.btnText?.value
                }
              </motion.a>
            </motion.div>
          </div>
        </section>

        <br />
        <br />
        <br />
      </div>
    </>
  );
};

export default ContactPage;
